import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  TextField,
  Box,
  IconButton,
  Tooltip
} from '@mui/material';
import { ContentCopy, Refresh } from '@mui/icons-material';
import color from 'utils/colors';

function SetPasswordDialog({ open, onClose, user, onConfirm, loading }) {
  const [password, setPassword] = useState('');

  const generateRandomPassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789!@#$%&*';
    let result = '';
    for (let i = 0; i < 12; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setPassword(result);
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(password);
    } catch (err) {
      console.error('Error copying to clipboard:', err);
    }
  };

  const handleConfirm = () => {
    if (password.trim()) {
      onConfirm(user, password.trim());
    }
  };

  const handleClose = () => {
    setPassword('');
    onClose();
  };

  React.useEffect(() => {
    if (open) {
      generateRandomPassword();
    }
  }, [open]);

  if (!user) return null;

  const displayName = user.names && user.surnames 
    ? `${user.names} ${user.surnames}`.trim()
    : user.names 
      ? user.names
      : user.name || 'Usuario';

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Typography 
          variant="h6" 
          sx={{ 
            color: color.purpleSecondary,
            fontWeight: 'bold'
          }}
        >
          Setear Contraseña
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Typography variant="body1" sx={{ mb: 2 }}>
          Configurar nueva contraseña para <strong>{displayName}</strong>
        </Typography>
        
        <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
          Se enviará un correo a <strong>{user.email}</strong> con la nueva contraseña.
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TextField
            fullWidth
            label="Nueva Contraseña"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            variant="outlined"
            size="small"
            placeholder="Ingrese la contraseña o genere una aleatoria"
          />
          
          <Tooltip title="Generar contraseña aleatoria">
            <IconButton 
              onClick={generateRandomPassword}
              sx={{ 
                color: color.purpleSecondary,
                '&:hover': { backgroundColor: `${color.purpleSecondary}10` }
              }}
            >
              <Refresh />
            </IconButton>
          </Tooltip>

          <Tooltip title="Copiar al portapapeles">
            <IconButton 
              onClick={copyToClipboard}
              disabled={!password.trim()}
              sx={{ 
                color: color.purpleSecondary,
                '&:hover': { backgroundColor: `${color.purpleSecondary}10` }
              }}
            >
              <ContentCopy />
            </IconButton>
          </Tooltip>
        </Box>
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 3 }}>
        <Button 
          onClick={handleClose}
          variant="outlined"
          sx={{
            borderColor: color.purpleSecondary,
            color: color.purpleSecondary,
            '&:hover': {
              borderColor: color.purpleSecondary,
              backgroundColor: `${color.purpleSecondary}10`,
            }
          }}
        >
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={!password.trim() || loading}
          sx={{
            backgroundColor: color.purpleSecondary,
            '&:hover': {
              backgroundColor: color.purpleSecondary,
            }
          }}
        >
          {loading ? 'Guardando...' : 'Guardar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default SetPasswordDialog;